import React, { useState, useEffect } from 'react';
import { AlertCircle, Mail, X } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'sonner';

const EmailVerificationBanner = ({ userEmail, onDismiss }) => {
  const [isResending, setIsResending] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(null);
  const [isVisible, setIsVisible] = useState(true);

  // Check localStorage for cooldown and hide state
  useEffect(() => {
    const hideUntil = localStorage.getItem('emailBannerHideUntil');
    if (hideUntil) {
      const hideTime = new Date(hideUntil);
      if (hideTime > new Date()) {
        setIsVisible(false);
        return;
      } else {
        localStorage.removeItem('emailBannerHideUntil');
      }
    }

    const cooldownUntil = localStorage.getItem('emailResendCooldownUntil');
    if (cooldownUntil) {
      const cooldownDate = new Date(cooldownUntil);
      if (cooldownDate > new Date()) {
        setCooldownTime(cooldownDate);
      } else {
        localStorage.removeItem('emailResendCooldownUntil');
      }
    }
  }, []);

  // Countdown timer for cooldown
  useEffect(() => {
    if (!cooldownTime) return;

    const interval = setInterval(() => {
      if (new Date() >= cooldownTime) {
        setCooldownTime(null);
        localStorage.removeItem('emailResendCooldownUntil');
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldownTime]);

  // Listen for cooldown updates from other components
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'emailResendCooldownUntil' && e.newValue) {
        setCooldownTime(new Date(e.newValue));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleResendEmail = async () => {
    if (cooldownTime) return;

    setIsResending(true);
    try {
      const functions = getFunctions();
      const resendEmail = httpsCallable(functions, 'apiResendEmailVerification');
      const result = await resendEmail();

      if (result.data.success) {
        toast.success('Verification email sent! Please check your inbox.');
        
        // Set 10-minute cooldown
        const cooldownUntil = new Date(Date.now() + 10 * 60 * 1000);
        localStorage.setItem('emailResendCooldownUntil', cooldownUntil.toISOString());
        setCooldownTime(cooldownUntil);
        
        // Hide banner for 10 minutes
        localStorage.setItem('emailBannerHideUntil', cooldownUntil.toISOString());
        setIsVisible(false);
      } else if (result.data.alreadyVerified) {
        toast.info('Your email is already verified!');
        setIsVisible(false);
      } else if (result.data.rateLimited) {
        toast.error(`Please wait ${result.data.waitMinutes} minutes before requesting another email.`);
      } else {
        toast.error(result.data.message || 'Failed to send verification email');
      }
    } catch (error) {
      console.error('Error resending verification email:', error);
      toast.error('Failed to send verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleDismiss = () => {
    const hideUntil = new Date(Date.now() + 10 * 60 * 1000); // Hide for 10 minutes
    localStorage.setItem('emailBannerHideUntil', hideUntil.toISOString());
    setIsVisible(false);
    if (onDismiss) onDismiss();
  };

  const getRemainingTime = () => {
    if (!cooldownTime) return null;
    const diff = cooldownTime - new Date();
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!isVisible) return null;

  return (
    <div className="bg-yellow-50 border-b border-yellow-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-1">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-yellow-800">
                <span className="font-semibold">Email Verification Required:</span> Please verify your email address to receive your{' '}
                <span className="font-bold">100 FREE credits</span> and unlock full access.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleResendEmail}
              disabled={isResending || !!cooldownTime}
              className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700 disabled:bg-yellow-300 disabled:cursor-not-allowed transition-colors"
            >
              <Mail className="w-4 h-4" />
              {isResending ? 'Sending...' : cooldownTime ? `Wait ${getRemainingTime()}` : 'Resend Email'}
            </button>
            <button
              onClick={handleDismiss}
              className="p-2 text-yellow-600 hover:text-yellow-800 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
